import { identityGateway } from '../../identity/services';
import { mockWorkLocationRegistry } from '../../../shared/mockWorkLocationRegistry';
import { ApiError } from '../../identity/types';
import type {
  AddressDraft,
  AddressStepResponse,
  BasicInfoDraft,
  BasicInfoStepResponse,
  BusinessProfileResponse,
  CreateFundingAccountRequest,
  CreateMembershipInvitationRequest,
  CreatePayScheduleRequest,
  FundingAccountDraftResponse,
  FundingAccountResponse,
  MembershipInvitationResponse,
  MembershipResponse,
  PayScheduleDraftResponse,
  PayScheduleResponse,
  SaveAddressRequest,
  SaveFundingAccountDraftRequest,
  SavePayScheduleDraftRequest,
  SaveWorkLocationDraftRequest,
  SubmitBusinessProfileResponse,
  WorkLocationDraftResponse,
  WorkLocationRequest,
  WorkLocationResponse,
} from '../types';
import type { IBusinessProfileSetupGateway } from './IBusinessProfileSetupGateway';
import { buildMockCompanyFixture } from './mockExistingCompanyFixture';
import { buildStep5ShowcaseLocations, type ShowcaseIds } from './mockStep5ShowcaseData';
import {
  applyRegistration,
  badRequest,
  buildMockRegistration,
  cloneLocation,
  sameAddress,
  validateMockAddress,
  validateMockMailing,
} from './mockWorkLocationRules';

interface CompanyMockState {
  basicInfo: Omit<BasicInfoStepResponse, 'draft'>;
  basicInfoDraft: BasicInfoDraft | null;
  address: Omit<AddressStepResponse, 'draft'>;
  addressDraft: AddressDraft | null;

  fundingAccount: FundingAccountResponse | null;
  fundingAccountDraft: FundingAccountDraftResponse | null;

  paySchedule: PayScheduleResponse | null;
  payScheduleDraft: PayScheduleDraftResponse | null;

  workLocations: WorkLocationResponse[];
  workLocationDraft: WorkLocationDraftResponse | null;
  nextLocationSequence: number;
  /** Ids of locations that still have (synthetic) assigned employees: archive and take-out-of-payroll are refused for them. */
  assignedLocationIds: Set<string>;

  members: MembershipResponse[];
  invitations: MembershipInvitationResponse[];
  nextInvitationSequence: number;
}

function createInitialState(companyId: string, companyName: string): CompanyMockState {
  const fixture = buildMockCompanyFixture(companyId, companyName);
  return {
    basicInfo: fixture.basicInfo,
    basicInfoDraft: null,
    address: fixture.address,
    addressDraft: null,
    fundingAccount: fixture.fundingAccount,
    fundingAccountDraft: null,
    paySchedule: fixture.paySchedule,
    payScheduleDraft: null,
    workLocations: fixture.workLocations,
    workLocationDraft: null,
    nextLocationSequence: fixture.workLocations.length + 1,
    assignedLocationIds: new Set<string>(),
    members: fixture.members,
    invitations: fixture.invitations,
    nextInvitationSequence: 2,
  };
}

/**
 * Development/QA-only mock — selected instead of HttpBusinessProfileSetupGateway when
 * VITE_USE_MOCKS=true (see BusinessProfileSetupGateway.ts). Models Company Details as belonging to
 * WHICHEVER company the signed-in Admin currently has active, not one hardcoded company: every method
 * below resolves the real `identityGateway.getMe().activeCompanyId` at call time (reusing the same
 * identity/membership mock store every other Mock*Gateway already reads, rather than a separate,
 * disconnected identity source — see mockExistingCompanyFixture.ts's own doc comment) and looks up (or
 * lazily creates, on first access) that company's own in-memory fixture. Switching the active company via
 * BusinessViewSwitcher therefore changes what Company Details shows, exactly like the real backend's
 * session/company-scoped persistence would. State is module-singleton-scoped (resets on a full page
 * reload), matching MockIdentityGateway/MockCompaniesGateway's own established pattern. Real HTTP calls are
 * never made from this class.
 */
/** Keeps the people mock's work-location list in step with this one (EPN-233). The main location is already part of the people mock's own seed. */
function shareWithPeopleMock(location: WorkLocationResponse): void {
  if (location.isDefault) return;
  mockWorkLocationRegistry.upsert({ id: location.id, name: location.name, locationCode: location.locationCode, status: location.status, isDefault: false });
}

class MockBusinessProfileSetupGateway implements IBusinessProfileSetupGateway {
  private byCompany = new Map<string, CompanyMockState>();

  private async resolveCompany(): Promise<CompanyMockState> {
    const me = await identityGateway.getMe();
    const companyId = me.activeCompanyId;
    if (!companyId) {
      throw new ApiError(400, 'No active company is selected.');
    }
    let state = this.byCompany.get(companyId);
    if (!state) {
      const companyName = me.memberships.find((m) => m.companyId === companyId)?.companyName ?? 'Unknown Company';
      state = createInitialState(companyId, companyName);
      // The signed-in Admin IS the company's first Owner in this mock, so "who may set Owner" follows the real session like the server does.
      const owner = state.members[0];
      if (owner) {
        owner.userId = me.userId;
        owner.email = me.email;
        owner.displayName = me.displayName ?? owner.displayName ?? null;
      }
      this.byCompany.set(companyId, state);
    }
    return state;
  }

  // ---- Overview / Final Submit ----

  async getProfile(): Promise<BusinessProfileResponse> {
    const me = await identityGateway.getMe();
    const companyId = me.activeCompanyId ?? '';
    const state = await this.resolveCompany();
    return {
      companyId,
      status: 'Completed',
      basicInfo: { ...state.basicInfo, draft: state.basicInfoDraft },
      address: { ...state.address, draft: state.addressDraft },
    };
  }

  async submit(): Promise<SubmitBusinessProfileResponse> {
    return { completed: true, completedAtUtc: new Date().toISOString(), missingSteps: [] };
  }

  async clearDraft(): Promise<void> {
    const state = await this.resolveCompany();
    state.basicInfoDraft = null;
    state.addressDraft = null;
  }

  // ---- Step 1: Basic Business Info ----

  async saveBasicInfoDraft(request: BasicInfoDraft): Promise<BasicInfoStepResponse> {
    const state = await this.resolveCompany();
    state.basicInfoDraft = { ...request };
    return { ...state.basicInfo, draft: state.basicInfoDraft };
  }

  async saveBasicInfoAndContinue(request: BasicInfoDraft): Promise<BasicInfoStepResponse> {
    const state = await this.resolveCompany();
    state.basicInfo = { ...state.basicInfo, ...request, isComplete: true };
    state.basicInfoDraft = null;
    return { ...state.basicInfo, draft: null };
  }

  // ---- Step 2: Business Address ----

  async saveAddressDraft(request: SaveAddressRequest): Promise<AddressStepResponse> {
    const state = await this.resolveCompany();
    state.addressDraft = { ...request };
    return { ...state.address, draft: state.addressDraft };
  }

  async saveAddressAndContinue(request: SaveAddressRequest): Promise<AddressStepResponse> {
    const state = await this.resolveCompany();
    state.address = { ...state.address, ...request, isComplete: true };
    state.addressDraft = null;
    return { ...state.address, draft: null };
  }

  // ---- Step 3: Bank Information ----

  async getFundingAccount(): Promise<FundingAccountResponse | null> {
    const state = await this.resolveCompany();
    return state.fundingAccount ? { ...state.fundingAccount } : null;
  }

  async saveFundingAccount(request: CreateFundingAccountRequest): Promise<FundingAccountResponse> {
    const state = await this.resolveCompany();
    // Never retains the raw routing/account number — only the masked last 4, exactly like the real
    // backend's own persistence guarantee (see CompanyFundingAccount's own doc comment).
    state.fundingAccount = {
      id: state.fundingAccount?.id ?? 'mock-funding-account-1',
      bankName: request.bankName,
      maskedAccountLast4: request.accountNumber.slice(-4),
      accountType: request.accountType,
      fundingPartner: state.fundingAccount?.fundingPartner ?? (state.paySchedule ? 'NatPay' : null),
      verificationState: 'Pending',
    };
    state.fundingAccountDraft = null;
    return { ...state.fundingAccount };
  }

  async getFundingAccountDraft(): Promise<FundingAccountDraftResponse | null> {
    const state = await this.resolveCompany();
    return state.fundingAccountDraft ? { ...state.fundingAccountDraft } : null;
  }

  async saveFundingAccountDraft(request: SaveFundingAccountDraftRequest): Promise<FundingAccountDraftResponse> {
    const state = await this.resolveCompany();
    state.fundingAccountDraft = { ...request };
    return { ...state.fundingAccountDraft };
  }

  async clearFundingAccountDraft(): Promise<void> {
    const state = await this.resolveCompany();
    state.fundingAccountDraft = null;
  }

  // ---- Step 4: Payroll Setup ----

  async getActivePaySchedule(): Promise<PayScheduleResponse | null> {
    const state = await this.resolveCompany();
    return state.paySchedule ? { ...state.paySchedule } : null;
  }

  async savePaySchedule(request: CreatePayScheduleRequest): Promise<PayScheduleResponse> {
    const me = await identityGateway.getMe();
    const state = await this.resolveCompany();
    state.paySchedule = {
      id: state.paySchedule?.id ?? 'mock-pay-schedule-1',
      companyId: me.activeCompanyId ?? '',
      name: request.name,
      frequency: request.frequency,
      firstPayDay: request.firstPayDay,
      firstPayDayKind: request.firstPayDayKind ?? 1,
      secondPayDay: request.secondPayDay ?? null,
      secondPayDayKind: request.secondPayDayKind ?? null,
      payPeriodStartDate: request.payPeriodStartDate,
      isDefault: true,
      isActive: true,
      createdAtUtc: state.paySchedule?.createdAtUtc ?? new Date().toISOString(),
      updatedAtUtc: new Date().toISOString(),
      firstPayDate: request.firstPayDate ?? null,
    };
    state.payScheduleDraft = null;
    return { ...state.paySchedule };
  }

  async getPayScheduleDraft(): Promise<PayScheduleDraftResponse | null> {
    const state = await this.resolveCompany();
    return state.payScheduleDraft ? { ...state.payScheduleDraft } : null;
  }

  async savePayScheduleDraft(request: SavePayScheduleDraftRequest): Promise<PayScheduleDraftResponse> {
    const state = await this.resolveCompany();
    state.payScheduleDraft = { ...request };
    return { ...state.payScheduleDraft };
  }

  async clearPayScheduleDraft(): Promise<void> {
    const state = await this.resolveCompany();
    state.payScheduleDraft = null;
  }

  // ---- Step 5: Branches / Work Locations ----
  // The mock mirrors the server's Step 5 rules (registration validation, masked account numbers, carry-forward, stale-version conflicts,
  // main-location protection and the archive guard) so the UI is exercised against the same behavior the API enforces.

  async listWorkLocations(): Promise<WorkLocationResponse[]> {
    const state = await this.resolveCompany();
    return state.workLocations.map(cloneLocation);
  }

  async createWorkLocation(request: WorkLocationRequest): Promise<WorkLocationResponse> {
    const state = await this.resolveCompany();
    validateMockAddress(request);
    const mailing = validateMockMailing(request);
    if (state.workLocations.some((l) => sameAddress(l, request))) throw badRequest('Another work location already has this exact address.');
    const registration = request.taxRegistration ? buildMockRegistration(request.taxRegistration, null) : null;
    const location: WorkLocationResponse = {
      id: `mock-location-${state.nextLocationSequence}`,
      name: request.name,
      addressLine1: request.addressLine1,
      addressLine2: request.addressLine2,
      city: request.city,
      state: request.state,
      zip: request.zip,
      country: request.country,
      county: request.county,
      phone: request.phone,
      locationType: String(request.locationType) as WorkLocationResponse['locationType'],
      locationCode: `LOC-${String(state.nextLocationSequence).padStart(3, '0')}`,
      timeZone: null,
      status: request.activeForPayroll === false ? 'Inactive' : 'Active',
      isDefault: false,
      createdAtUtc: new Date().toISOString(),
      updatedAtUtc: null,
      duplicateNameWarning: state.workLocations.some((l) => l.status === 'Active' && l.name.toLowerCase() === request.name.toLowerCase())
        ? 'Another active work location already uses this name.'
        : null,
      activeForPayroll: request.activeForPayroll !== false,
      version: 0,
      mailingAddress: mailing,
      taxRegistration: null,
      taxRegistrationStatus: 'NotRegistered',
      registrationComplete: false,
      registrationIssues: [],
    };
    applyRegistration(location, registration);
    state.nextLocationSequence += 1;
    state.workLocations.push(location);
    state.workLocationDraft = null;
    shareWithPeopleMock(location);
    return cloneLocation(location);
  }

  async updateWorkLocation(id: string, request: WorkLocationRequest): Promise<WorkLocationResponse> {
    const state = await this.resolveCompany();
    const existing = state.workLocations.find((l) => l.id === id);
    if (!existing) throw new ApiError(404, 'Work location not found.');
    if (request.expectedVersion !== undefined && request.expectedVersion !== existing.version) {
      throw new ApiError(
        409,
        'This location was changed by someone else after you opened it. Reload it to see the latest details, then make your change again.',
        null, true, 'WORK_LOCATION_VERSION_CONFLICT',
      );
    }
    validateMockAddress(request);
    const mailing = validateMockMailing(request);
    if (existing.isDefault) {
      if (existing.addressLine1 !== request.addressLine1 || existing.city !== request.city || existing.state !== request.state || existing.zip !== request.zip) {
        throw badRequest("The main location's address comes from Company Address. Change it in Step 2, Company Address.");
      }
      if (request.activeForPayroll === false) throw badRequest("The company's default/main work location must stay active for payroll.");
    }
    if (state.workLocations.some((l) => l.id !== id && sameAddress(l, request))) throw badRequest('Another work location already has this exact address.');
    const registration = request.taxRegistration ? buildMockRegistration(request.taxRegistration, existing.taxRegistration) : undefined;

    const nextStatus: WorkLocationResponse['status'] = existing.status === 'Archived' ? 'Archived' : request.activeForPayroll === false ? 'Inactive' : 'Active';
    if (existing.status === 'Active' && nextStatus === 'Inactive') this.guardAgainstAssignments(state, existing, 'taken out of payroll');

    Object.assign(existing, {
      name: request.name,
      locationType: existing.isDefault ? null : (String(request.locationType) as WorkLocationResponse['locationType']),
      addressLine1: request.addressLine1,
      addressLine2: request.addressLine2,
      city: request.city,
      state: request.state,
      zip: request.zip,
      country: request.country,
      county: request.county,
      phone: request.phone,
      status: nextStatus,
      activeForPayroll: nextStatus === 'Active',
      mailingAddress: mailing,
      version: existing.version + 1,
      updatedAtUtc: new Date().toISOString(),
    });
    if (registration !== undefined) applyRegistration(existing, registration);
    state.workLocationDraft = null;
    shareWithPeopleMock(existing);
    return cloneLocation(existing);
  }

  async archiveWorkLocation(id: string): Promise<WorkLocationResponse> {
    const state = await this.resolveCompany();
    const existing = state.workLocations.find((l) => l.id === id);
    if (!existing) throw new ApiError(404, 'Work location not found.');
    if (existing.isDefault) throw badRequest("The company's default/main work location cannot be archived.");
    this.guardAgainstAssignments(state, existing, 'archived');
    existing.status = 'Archived';
    existing.activeForPayroll = false;
    existing.version += 1;
    existing.updatedAtUtc = new Date().toISOString();
    shareWithPeopleMock(existing);
    return cloneLocation(existing);
  }

  async reactivateWorkLocation(id: string): Promise<WorkLocationResponse> {
    const state = await this.resolveCompany();
    const existing = state.workLocations.find((l) => l.id === id);
    if (!existing) throw new ApiError(404, 'Work location not found.');
    if (existing.status === 'Active') throw badRequest('Only an archived work location can be reactivated.');
    existing.status = 'Active';
    existing.activeForPayroll = true;
    existing.version += 1;
    existing.updatedAtUtc = new Date().toISOString();
    shareWithPeopleMock(existing);
    return cloneLocation(existing);
  }

  async deleteWorkLocation(id: string): Promise<void> {
    const state = await this.resolveCompany();
    const existing = state.workLocations.find((l) => l.id === id);
    if (existing?.taxRegistration) throw badRequest('This location has a payroll tax registration on record and cannot be deleted. Archive it instead.');
    state.workLocations = state.workLocations.filter((l) => l.id !== id);
    mockWorkLocationRegistry.remove(id);
  }

  async getWorkLocationDraft(): Promise<WorkLocationDraftResponse | null> {
    const state = await this.resolveCompany();
    return state.workLocationDraft ? { ...state.workLocationDraft } : null;
  }

  async saveWorkLocationDraft(request: SaveWorkLocationDraftRequest): Promise<WorkLocationDraftResponse> {
    const state = await this.resolveCompany();
    state.workLocationDraft = { ...request, locationType: request.locationType ? (String(request.locationType) as WorkLocationDraftResponse['locationType']) : null };
    return { ...state.workLocationDraft };
  }

  async clearWorkLocationDraft(): Promise<void> {
    const state = await this.resolveCompany();
    state.workLocationDraft = null;
  }

  /** The archive / take-out-of-payroll guard: refused while the (synthetic) assigned-employee set contains the location. */
  private guardAgainstAssignments(state: CompanyMockState, location: WorkLocationResponse, action: string): void {
    if (this.unverifiableAssignments) {
      this.unverifiableAssignments = false;
      throw new ApiError(
        503,
        "We couldn't verify whether employees are assigned to this location right now. Nothing was changed. Please try again.",
        null, true, null,
      );
    }
    if (state.assignedLocationIds.has(location.id)) {
      throw new ApiError(
        409,
        `There are currently active employees assigned to this location. You must reassign all employees to another location before it can be ${action}.`,
        null, true, 'WORK_LOCATION_HAS_ASSIGNED_EMPLOYEES',
      );
    }
  }

  // ---- Test hooks (window.__epnMockBusinessProfile, only reachable when the mock gateway is in use) ----

  private unverifiableAssignments = false;

  /** Marks which of the active company's locations still have (synthetic) assigned employees. Only for tests and screenshot capture. */
  async setAssignedLocationsForTests(ids: string[]): Promise<void> {
    const state = await this.resolveCompany();
    state.assignedLocationIds = new Set(ids);
  }

  /** The next archive attempt fails as "could not verify assignments" (503), then behaves normally again. */
  failNextAssignmentCheckForTests(): void {
    this.unverifiableAssignments = true;
  }

  /** Bumps a location's version behind the form's back, simulating another admin saving first. */
  async touchLocationForTests(id: string): Promise<void> {
    const state = await this.resolveCompany();
    const existing = state.workLocations.find((l) => l.id === id);
    if (existing) existing.version += 1;
  }

  /** Step 6: fills the active company with `count` further synthetic Admins (the first is the signed-in Owner) plus two pending invitations, so
   * the many-Admins overflow can be exercised. Only for tests and screenshot capture. */
  async seedStep6DataForTests(count: number): Promise<void> {
    const state = await this.resolveCompany();
    const first = state.members[0];
    const names = ['Jordan Lee', 'Priya Nair', 'Marcus Webb', 'Elena Rossi', 'Tomas Novak', 'Aiko Tanaka', 'Samir Haddad', 'Chloe Martin', 'Diego Alvarez', 'Hannah Berg'];
    state.members = [
      ...(first ? [first] : []),
      ...names.slice(0, Math.max(0, count - 1)).map((name, i): MembershipResponse => ({
        id: `mock-seed-member-${i + 1}`,
        userId: `mock-seed-user-${i + 1}`,
        email: `${name.toLowerCase().replace(/\s+/g, '.')}@example.test`,
        roleId: first?.roleId ?? 'role-company-admin',
        roleName: first?.roleName ?? 'CompanyAdmin',
        isActive: true,
        isOwner: false,
        displayName: name,
        officeNumber: `+1555555${String(100 + i).padStart(4, '0')}`,
        phone: `+1555555${String(200 + i).padStart(4, '0')}`,
      })),
    ];
  }

  /** Step 6: makes the signed-in Admin an Owner or not, to show the "only an Owner" states. Only for tests and screenshot capture. */
  async setCallerOwnerForTests(isOwner: boolean): Promise<void> {
    const state = await this.resolveCompany();
    const me = await identityGateway.getMe();
    const caller = state.members.find((m) => m.userId === me.userId);
    if (caller) caller.isOwner = isOwner;
    if (!isOwner && !state.members.some((m) => m.isOwner)) {
      const other = state.members.find((m) => m.userId !== me.userId);
      if (other) other.isOwner = true;
    }
  }

  /** Replaces the active company's locations with a synthetic set that exercises every list state (see mockStep5ScreenshotData.ts). */
  async seedStep5DataForTests(): Promise<ShowcaseIds> {
    const state = await this.resolveCompany();
    const seeded = buildStep5ShowcaseLocations(state.workLocations.find((l) => l.isDefault));
    state.workLocations = seeded.locations;
    state.nextLocationSequence = seeded.locations.length + 1;
    state.assignedLocationIds = new Set([seeded.ids.palmdale]);
    return seeded.ids;
  }

  // ---- Step 6: Admin Information ----

  async listMembers(_companyId: string): Promise<MembershipResponse[]> {
    const state = await this.resolveCompany();
    return state.members.map((m) => ({ ...m }));
  }

  async listInvitations(_companyId: string): Promise<MembershipInvitationResponse[]> {
    const state = await this.resolveCompany();
    return state.invitations.map((i) => ({ ...i }));
  }

  async createInvitation(_companyId: string, request: CreateMembershipInvitationRequest): Promise<MembershipInvitationResponse> {
    const state = await this.resolveCompany();
    // The same refusals the server makes (MembershipService.InviteAsync), so the screen is exercised against real behaviour.
    const me = await identityGateway.getMe();
    const callerIsOwner = state.members.some((m) => m.userId === me.userId && m.isOwner && m.isActive);
    if (request.isOwner && !callerIsOwner) throw new ApiError(400, 'Only an active Owner can invite someone as an Owner.');
    if (!request.name?.trim()) throw new ApiError(400, 'Admin name is required.');
    if (!request.officeNumber?.trim() || !request.phone?.trim()) throw new ApiError(400, 'Office number and phone are required.');
    const normalized = request.email.trim().toLowerCase();
    if (state.members.some((m) => m.email.toLowerCase() === normalized)) throw new ApiError(400, 'This person already has access to this company.');
    if (state.invitations.some((i) => i.inviteeEmail.toLowerCase() === normalized && i.status === 'Pending')) {
      throw new ApiError(400, 'An invitation to this email address is already pending for this company.');
    }
    const invitation: MembershipInvitationResponse = {
      id: `mock-invitation-${state.nextInvitationSequence}`,
      inviteeEmail: request.email,
      name: request.name,
      roleId: request.roleId,
      isOwner: request.isOwner,
      officeNumber: request.officeNumber,
      phone: request.phone,
      status: 'Pending',
      expiresAtUtc: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
    };
    state.nextInvitationSequence += 1;
    state.invitations.push(invitation);
    return { ...invitation };
  }

  async revokeInvitation(_companyId: string, invitationId: string): Promise<void> {
    const state = await this.resolveCompany();
    state.invitations = state.invitations.filter((i) => i.id !== invitationId);
  }

  async setOwnerStatus(_companyId: string, membershipId: string, isOwner: boolean): Promise<MembershipResponse> {
    const state = await this.resolveCompany();
    const existing = state.members.find((m) => m.id === membershipId);
    if (!existing) throw new Error(`Mock membership ${membershipId} not found.`);
    existing.isOwner = isOwner;
    return { ...existing };
  }
}

/** Typed as the class (not just the interface) so tests and the mock-mode window hook can reach the Step 5 test hooks. */
export const mockBusinessProfileSetupGateway = new MockBusinessProfileSetupGateway();
export type MockBusinessProfileSetupGatewayHooks = MockBusinessProfileSetupGateway;
